import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { MatchProvider } from "./lib/MatchContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import CommandCentre from "./pages/CommandCentre";
import MatchesPage from "./pages/MatchesPage";
import VenueReadinessPage from "./pages/VenueReadinessPage";
import RunSheetPage from "./pages/RunSheetPage";
import IncidentsPage from "./pages/IncidentsPage";
import ReadinessPage from "./pages/ReadinessPage";
import RaiseIssuePage from "./pages/RaiseIssuePage";
import PreMatchTrackerPage from "./pages/PreMatchTrackerPage";
import PostMatchExportPage from "./pages/PostMatchExportPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import StocksPage from "./pages/StocksPage";
import AllocationsPage from "./pages/AllocationsPage";
import CriticalInfoPage from "./pages/CriticalInfoPage";
import PostEventReportPage from "./pages/PostEventReportPage";
import ReportDepartmentPage from "./pages/ReportDepartmentPage";
import ReportDesignPage from "./pages/ReportDesignPage";
import ReportPreviewPage from "./pages/ReportPreviewPage";
import { Toaster } from "sonner";

function Protected({ children }) {
  const { user, checked } = useAuth();
  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian text-zinc-500 font-mono-live">
        ▓▒░ initialising command centre ░▒▓
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Vendors are hard-restricted to the two allow-listed paths. Any other route → tracker.
const VENDOR_ALLOWED = [
  "/matches",
  "/pre-match/tracker",
  "/pre-match/raise-issue",
  "/login",
];
function VendorRouteGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.role === "vendor" && !VENDOR_ALLOWED.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
    return <Navigate to="/pre-match/tracker" replace />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <MatchProvider>
      <BrowserRouter>
        <Toaster theme="light" position="top-right" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <Protected>
                <VendorRouteGuard>
                  <Layout />
                </VendorRouteGuard>
              </Protected>
            }
          >
            <Route index element={<CommandCentre />} />
            <Route path="matches" element={<MatchesPage />} />

            {/* Pre-Match section */}
            <Route path="pre-match/raise-issue" element={<RaiseIssuePage />} />
            <Route path="pre-match/tracker" element={<PreMatchTrackerPage />} />
            <Route path="pre-match/venue" element={<VenueReadinessPage />} />
            <Route path="pre-match/stocks" element={<StocksPage />} />
            <Route path="pre-match/allocations" element={<AllocationsPage />} />
            <Route path="critical-info" element={<Navigate to="/critical-info/maps" replace />} />
            <Route path="critical-info/:section" element={<CriticalInfoPage />} />

            {/* Match Day section */}
            <Route path="match-day/runsheet" element={<RunSheetPage />} />
            <Route path="match-day/incidents" element={<IncidentsPage />} />

            {/* Post-Match section */}
            <Route path="post-match/export" element={<PostMatchExportPage />} />
            <Route path="post-event-report" element={<PostEventReportPage />} />
            <Route path="post-event-report/dept/:dept" element={<ReportDepartmentPage />} />
            <Route path="post-event-report/design" element={<ReportDesignPage />} />
            <Route path="post-event-report/preview" element={<ReportPreviewPage />} />

            {/* Admin */}
            <Route path="admin/users" element={<AdminUsersPage />} />

            <Route path="readiness" element={<ReadinessPage />} />

            {/* Back-compat aliases so old links keep working */}
            <Route path="tasks" element={<Navigate to="/pre-match/tracker" replace />} />
            <Route path="match-day/tasks" element={<Navigate to="/pre-match/tracker" replace />} />
            <Route path="runsheet" element={<Navigate to="/match-day/runsheet" replace />} />
            <Route path="incidents" element={<Navigate to="/match-day/incidents" replace />} />
            <Route path="venue" element={<Navigate to="/pre-match/venue" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </MatchProvider>
    </AuthProvider>
  );
}
